/** @prettier */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { Trans } from 'react-i18next';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import Button from '@xanadu/ui-shared/components/widgets/Button';
import { useReduxBridge } from '@xanadu/ui-shared/contexts/ReduxBridgeContext';
import { useImprovedTranslation } from '@xanadu/ui-shared/hooks';
import { dispatch as gtmDispatch } from '@xanadu/ui-shared/interfaces/googleTagManager';
import { userVirtualPageView as gtmUserVirtualPageView } from '@xanadu/ui-shared/interfaces/googleTagManager/actions';
import { track } from '@xanadu/ui-shared/interfaces/woopra';
import { registrationRequest as registrationRequestAct } from '@xanadu/ui-shared/modules/userRegistration/actions';
import { loginRequest as loginRequestAct } from '@xanadu/ui-shared/modules/userSession/actions';
import showLiveSupport from '@xanadu/ui-shared/utils/showLiveSupport';

import { getClientString } from '../../utils';

import styles from './PreviousLoggedIn.module.scss';

export const INTERACTION_DELAY = 200;

const PreviousLoggedIn = ({ closeModal, loginRequest, registrationRequest }) => {
  const { clientName, clientSite } = useReduxBridge() || {};

  const { t } = useImprovedTranslation();

  const currentUrl = window.location.href;

  const handleRegister = () => {
    closeModal();
    setTimeout(() => {
      registrationRequest({ show: true, promoRegistrationImage: null, checked: true });

      // GTM
      gtmDispatch(
        gtmUserVirtualPageView({
          currentUrl,
          eventName: 'Reg Process',
          virtualPageTitle: 'Step 1 - account',
          virtualPageURL: '/reg/step1/',
          previousURL: '/login/',
          clickSource: { clickSource: `mb-join-now-button_${location.href}` },
        })
      );

      track('Registration', 'Registration opened', {
        description: 'login',
      });
    }, INTERACTION_DELAY);
  };

  const handleLogin = (e) => {
    e.preventDefault();

    closeModal();

    setTimeout(() => {
      // GTM
      gtmDispatch(
        gtmUserVirtualPageView({
          currentUrl,
          eventName: 'Login Request',
          virtualPageTitle: 'Log In / Sign Up',
          virtualPageURL: '/login/',
          previousURL: `${location.href}`,
          clickSource: { clickSource: `mb-join-now-button_${location.href}` },
        })
      );

      track('Marketing', 'Login opened', {
        description: 'header',
      });

      loginRequest();
    }, INTERACTION_DELAY);
  };

  const mainClass = classNames({
    [styles.main]: true,
  });

  const clientString = getClientString(clientName, clientSite);

  return (
    <div className={mainClass}>
      <div className={styles.modalTitle}>
        <Trans i18nKey="Welcome back to" site={clientString}>
          Welcome back to {{ site: clientString }}
        </Trans>
      </div>
      <div className={styles.container}>
        <div className={styles.content}>
          <div>
            <Trans i18nKey="It looks like you may already have an account with" site={clientString}>
              It looks like you may already have an account with {{ site: clientString }}. Only one
              account is allowed per customer.
            </Trans>
          </div>
          <div>
            {t(
              'Please log in below. If you have forgotten your password, this can be reset from the login screen.'
            )}
          </div>
          <div>
            <span>
              <Trans i18nKey="Need more help? Speak to our customer service team.">
                Need more help?
                <button className={styles.helpButton} onClick={showLiveSupport}>
                  Speak to our customer service team.
                </button>
              </Trans>
            </span>
          </div>
        </div>
        <div className={styles.containerActions}>
          <Button sizeType="wider" buttonType="flat" onClick={handleRegister}>
            {t('Continue to Register')}
          </Button>
          <Button
            className={styles.primaryButton}
            buttonType="alternative"
            sizeType="wider"
            disabled={false}
            success={false}
            dataHook="login-top"
            href="/login"
            onClick={handleLogin}
          >
            {t('Log In')}
          </Button>
        </div>
      </div>
    </div>
  );
};

PreviousLoggedIn.propTypes = {
  closeModal: PropTypes.func.isRequired,
  loginRequest: PropTypes.func.isRequired,
  registrationRequest: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
};

const mapDispatchToProps = {
  loginRequest: loginRequestAct,
  registrationRequest: registrationRequestAct,
};

export const Component = PreviousLoggedIn;

export default withRouter(connect(null, mapDispatchToProps)(PreviousLoggedIn));
